import { cn } from '@/lib/utils'

/**
 * Đường xu hướng nhỏ trong ô chỉ số. Lấy màu từ `currentColor` nên tự theo
 * tông của ô chứa nó. Nét 2px giữ nguyên độ dày khi SVG co giãn
 * (`vector-effect`), chấm cuối vẽ bằng HTML để không bị méo thành elip.
 *
 * Ít hơn 2 điểm hoặc toàn số 0 thì không vẽ gì: một đường phẳng dưới đáy chỉ
 * nói "không có dữ liệu" bằng một cách khó hiểu.
 */
export function Sparkline({
  values,
  label,
  className,
}: {
  values: number[]
  /** Mô tả cho trình đọc màn hình, ví dụ "Doanh số 30 ngày". */
  label: string
  className?: string
}) {
  const max = Math.max(0, ...values)
  if (values.length < 2 || max <= 0) return null

  const W = 100
  const H = 32
  const step = W / (values.length - 1)
  const y = (v: number) => H - 2 - (v / max) * (H - 4)
  const line = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(2)},${y(v).toFixed(2)}`).join(' ')
  const area = `${line} L${W},${H} L0,${H} Z`
  const lastTop = (y(values[values.length - 1] ?? 0) / H) * 100

  return (
    <div className={cn('relative h-9', className)} role="img" aria-label={label}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-full w-full overflow-visible" aria-hidden>
        <path d={area} fill="currentColor" fillOpacity={0.12} stroke="none" />
        <path
          d={line}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <span
        className="absolute right-0 size-2 -translate-y-1/2 translate-x-1/2 rounded-full bg-current ring-2 ring-[rgb(var(--tone-bg,var(--raise)))]"
        style={{ top: `${lastTop}%` }}
        aria-hidden
      />
    </div>
  )
}
